import ProductAutoComplete from "@/components/Filter/productAutocomplete";
import {
  addSetting,
  copySetting,
  removeSettingByIndex,
  setSettingByIndex,
  setSettingByKey,
  setValidationsByKey,
} from "@/features/commissionSlice";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import _ from "lodash";
import moment from "moment";
import React from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

const MemoProductBonusFormRow = React.memo(
  function ProductBonusFormRow({
    index,
    item,
    validations,
    disabled,
    productOptions,
    removeFormData,
    copyFormData,
    currentDate,
    handleFormData,
  }) {
    return (
      <Stack key={index} sx={{ my: 2 }} direction={"row"} spacing={2}>
        <Stack
          direction={"row"}
          justifyContent="space-between"
          alignItems={"flex-start"}
          sx={{ flex: 7 }}
          spacing={1}
        >
          {item.isDisabled && (
            <SubdirectoryArrowRightIcon color="warning"></SubdirectoryArrowRightIcon>
          )}
          <TextField
            disabled={disabled}
            error={!!validations[index]?.nameErr}
            helperText={validations[index]?.nameErr}
            sx={{ maxWidth: 200, minWidth: 200, flex: 0.5 }}
            value={item.name}
            id="outlined-basic"
            label="名稱"
            onChange={(event) => {
              handleFormData(index, "name", event.target.value);
            }}
            variant="outlined"
          />
          <FormControl fullWidth>
            <DatePicker
              disabled={item.isDisabled || disabled}
              id="startDate"
              name="startDate"
              label="開始日期"
              value={item.startDate}
              onChange={(value) => {
                handleFormData(
                  index,
                  "startDate",
                  moment(value).format("YYYY-MM-DD"),
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ width: "100%" }}
                  error={!!validations[index]?.startDateErr}
                />
              )}
            ></DatePicker>
            <FormHelperText error={!!validations[index]?.startDateErr}>
              {validations[index]?.startDateErr}
            </FormHelperText>
          </FormControl>
          <FormControl fullWidth>
            <DatePicker
              disabled={item.isDisabled || disabled}
              id="startDate"
              name="startDate"
              label="結束日期"
              minDate={moment(item.startDate)
                .startOf("month")
                .format("YYYY-MM-DD")}
              value={item.endDate ?? moment(currentDate).endOf("month")}
              onChange={(value) => {
                handleFormData(
                  index,
                  "endDate",
                  moment(value).format("YYYY-MM-DD"),
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ width: "100%" }}
                  error={!!validations[index]?.endDateErr}
                />
              )}
            ></DatePicker>
            <FormHelperText error={!!validations[index]?.endDateErr}>
              {validations[index]?.endDateErr}
            </FormHelperText>
          </FormControl>
        </Stack>
        <ProductAutoComplete
          multiple
          disabled={item.isDisabled}
          sx={{ width: 200, flex: 7 }}
          productOptions={productOptions}
          handleOnChange={(value) => {
            handleFormData(index, "productIds", value);
          }}
          selectedValue={item.productIds ?? []}
          error={!!validations[index]?.productIdsErr}
        ></ProductAutoComplete>
        <TextField
          disabled={disabled}
          error={!!validations[index]?.minQtyErr}
          helperText={validations[index]?.minQtyErr}
          sx={{ maxWidth: 110, minWidth: 110, flex: 0.5 }}
          value={item.minQty}
          type="number"
          id="outlined-basic"
          label="最少目標數量"
          onChange={(event) => {
            handleFormData(index, "minQty", event.target.value);
          }}
          variant="outlined"
          InputProps={{
            inputProps: {
              min: 0,
            },
            endAdornment: <InputAdornment position="end">件</InputAdornment>,
          }}
        />
        <TextField
          disabled={disabled}
          error={!!validations[index]?.maxQtyErr}
          helperText={validations[index]?.maxQtyErr}
          sx={{ maxWidth: 110, minWidth: 110, flex: 0.5 }}
          value={item.maxQty}
          type="number"
          id="outlined-basic"
          label="最多目標數量"
          onChange={(event) => {
            handleFormData(index, "maxQty", event.target.value);
          }}
          variant="outlined"
          InputProps={{
            inputProps: {
              min: 0,
            },
            endAdornment: <InputAdornment position="end">件</InputAdornment>,
          }}
        />
        <TextField
          disabled={disabled}
          error={!!validations[index]?.bounsErr}
          helperText={validations[index]?.bounsErr}
          sx={{ maxWidth: 180, minWidth: 100, flex: 0.5 }}
          value={item.bouns}
          type="number"
          id="outlined-basic"
          label="獎金金額"
          onChange={(event) => {
            handleFormData(index, "bouns", event.target.value);
          }}
          variant="outlined"
          InputProps={{
            inputProps: {
              min: 0,
            },
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
        {!disabled && (
          <Stack
            direction={"row"}
            justifyContent="space-between"
            alignItems={"flex-start"}
            sx={{ flex: 1 }}
          >
            <IconButton onClick={() => copyFormData(item, index)}>
              <ContentCopyIcon></ContentCopyIcon>
            </IconButton>
            <IconButton color="error" onClick={() => removeFormData(index)}>
              <DeleteForeverIcon></DeleteForeverIcon>
            </IconButton>
          </Stack>
        )}
      </Stack>
    );
  },
  (prev, next) => {
    return (
      prev.formData.length === next.formData.length &&
      _.isEqual(prev.item, next.item) &&
      _.isEqual(prev.validations, next.validations)
    );
  },
);

export default function ProductBonusFormRows({ disabled, productOptions }) {
  const dispatch = useDispatch();

  const currentDate = useSelector(
    (state) => state.commission.settings.currentDate,
  );

  const formData = useSelector(
    (state) => state.commission.settings.productBonus,
    shallowEqual,
  );

  const validations = useSelector(
    (state) => state.commission.validations.productBonus,
    shallowEqual,
  );
  const removeFormData = React.useCallback(
    async (currentIndex) => {
      if (formData[currentIndex].isDisabled) {
        dispatch(
          removeSettingByIndex({
            key: "productBonus",
            index: currentIndex,
          }),
        );
        return;
      }

      const subItem = formData.find(
        (row) => formData[currentIndex].id === row.parentId,
      );
      if (subItem) {
        const newRow = formData.filter(
          (row) =>
            formData[currentIndex].id !== row.parentId &&
            formData[currentIndex].id !== row.id,
        );
        dispatch(
          setSettingByKey({
            key: "productBonus",
            value: newRow,
          }),
        );
      } else {
        dispatch(
          removeSettingByIndex({
            key: "productBonus",
            index: currentIndex,
          }),
        );
      }
    },
    [dispatch, formData],
  );
  const copyFormData = React.useCallback(
    async (item, index) => {
      if (item.productIds.length <= 0 || !item.maxQty || !item.name) {
        const tempValidations = Array.from({ length: formData.length }, () => [
          {},
        ]);
        if (!item.name) {
          tempValidations[index]["nameErr"] = "不能為空";
        }
        if (item.productIds.length <= 0) {
          tempValidations[index]["productIdsErr"] = "不能為空";
        }
        if (!item.maxQty) {
          tempValidations[index]["maxQtyErr"] = "不能為空";
        }
        dispatch(
          setValidationsByKey({
            key: "productBonus",
            value: tempValidations,
          }),
        );
        return;
      }
      dispatch(
        setValidationsByKey({
          key: "productBonus",
          value: [],
        }),
      );
      dispatch(
        copySetting({
          key: "productBonus",
          index: index,
          value: {
            ...item,
            parentId: item.parentId ?? item.id,
            minQty: parseInt(item.maxQty) + 1,
            maxQty: 0,
            name: "",
            isDisabled: true,
          },
        }),
      );
    },
    [dispatch, formData],
  );
  const handleFormData = React.useCallback(
    async (currentIndex, key, value) => {
      if (["startDate", "endDate", "productIds"].includes(key)) {
        const cloneRows = _.cloneDeep(formData);
        _.forEach(formData, (data, index) => {
          if (
            data.id === formData[currentIndex].id ||
            data.parentId === formData[currentIndex].id
          ) {
            cloneRows[index][key] = value;
          }
        });
        dispatch(
          setSettingByKey({
            key: "productBonus",
            value: cloneRows,
          }),
        );
      } else {
        const cloneRow = { ...formData[currentIndex] };
        cloneRow[key] = value;
        dispatch(
          setSettingByIndex({
            key: "productBonus",
            value: cloneRow,
            index: currentIndex,
          }),
        );
      }
    },
    [dispatch, formData],
  );
  const createFormData = React.useCallback(() => {
    dispatch(
      addSetting({
        key: "productBonus",
        value: {
          startDate: moment(currentDate).format("YYYY-MM-DD"),
          endDate: moment(currentDate).endOf("month").format("YYYY-MM-DD"),
          excludeCommission: true,
          minQty: -999,
          productIds: [],
        },
      }),
    );
  }, [dispatch, currentDate]);
  return (
    <React.Fragment>
      {_.map(formData, (item, index) => (
        <MemoProductBonusFormRow
          index={index}
          item={item}
          formData={formData}
          key={item.id}
          validations={validations}
          disabled={disabled}
          removeFormData={removeFormData}
          copyFormData={copyFormData}
          productOptions={productOptions}
          currentDate={currentDate}
          handleFormData={handleFormData}
        ></MemoProductBonusFormRow>
      ))}
      {!disabled && (
        <Box>
          <IconButton component="label" onClick={createFormData}>
            <AddBoxOutlinedIcon />
          </IconButton>
        </Box>
      )}
    </React.Fragment>
  );
}
